import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { CheckCircle2, Briefcase, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DEPARTMENTS = [
  { value: "team", label: "Team — counsellor applications & KYC" },
  { value: "book", label: "Book — bookings, payments, reschedules" },
  { value: "help", label: "Help — client support & queries" },
  { value: "official", label: "Official — brand & social media" },
];

export default function Serve() {
  const { user, loading } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", mobile: "", email: user?.email || "", username: user?.username || "", department: "team", experience: "" });
  const [eligibility, setEligibility] = useState(null); // null=loading, {blocked, domain}
  useEffect(() => {
    if (user) {
      setF(prev => ({ ...prev, name: user.name || prev.name, email: user.email || prev.email, username: user.username || prev.username }));
      api.get("/me/apply-eligibility?target=employee")
        .then(r => setEligibility(r.data))
        .catch(() => setEligibility({ blocked: false, domain: null }));
    }
  }, [user]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/employee-applications", f);
      setDone(true);
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="p-16 text-center text-sm">Loading…</div>;

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <Sparkles className="w-10 h-10 mx-auto" style={{ color: "var(--brand)" }} />
          <h1 className="font-serif-display text-3xl mt-4">Sign in to apply</h1>
          <p className="text-sm mt-3" style={{ color: "var(--text-secondary)" }}>
            Verify your identity with Google first — no password to create — then fill in your application.
          </p>
          <Link data-testid="serve-signin-btn" to="/login?next=/serve" className="btn-primary mt-8 inline-block">Continue with Google</Link>
        </div>
      </div>
    );
  }

  if (eligibility === null) return <div className="p-16 text-center text-sm">Loading…</div>;

  if (eligibility.blocked) {
    const domainLabel = { client: "client", counselor: "counselor", "counselor applicant": "counselor applicant", admin: "admin" }[eligibility.domain] || eligibility.domain;
    const article = /^[aeiou]/i.test(domainLabel) ? "an" : "a";
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="card-soft p-10">
          <h1 className="font-serif-display text-3xl mt-4">Already registered as {article} {domainLabel}</h1>
          <p className="text-sm mt-3" data-testid="serve-cross-domain-message" style={{ color: "var(--text-secondary)" }}>
            You are already registered as {article} {domainLabel}. Kindly go back and choose another Google account to continue to the employee application.
          </p>
          <Link data-testid="serve-cross-domain-back" to="/dashboard" className="btn-primary mt-8 inline-block">Go back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="serve-success">
        <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--brand)" }} />
        <h1 className="font-serif-display text-3xl mb-3">Application received</h1>
        <p style={{ color: "var(--text-secondary)" }}>
          We'll review it and email you at <strong>{f.email}</strong> if you're approved, with your
          section Serve ID (an internal reference) and salary details — you'll then sign in with
          Google or Apple using this email, no password needed.
        </p>
        <Link to="/" className="btn-outline inline-flex mt-8">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="flex items-center gap-2 mb-2">
        <Briefcase className="w-5 h-5" style={{ color: "var(--brand)" }} />
        <span className="label-eyebrow">Careers</span>
      </div>
      <h1 className="font-serif-display text-4xl mb-3">Serve with Counseltaria™</h1>
      <p className="mb-8" style={{ color: "var(--text-secondary)" }}>
        Join our internal team across Team, Book, Help or Official — apply below.
      </p>
      <form onSubmit={submit} className="card-soft p-8 space-y-5">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Full name</label>
            <input required data-testid="serve-name" value={f.name} onChange={e => set("name", e.target.value)}
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Mobile number</label>
            <input required data-testid="serve-mobile" value={f.mobile} onChange={e => set("mobile", e.target.value)}
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Personal email (verified via Google)</label>
            <input required disabled type="email" data-testid="serve-email" value={f.email}
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 bg-[color:var(--surface)] cursor-not-allowed opacity-80" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Username</label>
            <input required data-testid="serve-username" maxLength={24} value={f.username} onChange={e => set("username", e.target.value)}
              placeholder="e.g. writer_23"
              className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)]" />
          </div>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Post / section applied for</label>
          <select required data-testid="serve-department" value={f.department} onChange={e => set("department", e.target.value)}
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] bg-white">
            {DEPARTMENTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-2">Relevant experience</label>
          <textarea required data-testid="serve-experience" rows={4} value={f.experience} onChange={e => set("experience", e.target.value)}
            placeholder="Tell us about your relevant experience for this role…"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] resize-none" />
        </div>
        {err && <div data-testid="serve-error" className="text-sm" style={{ color: "var(--accent)" }}>{err}</div>}
        <button data-testid="serve-submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Submitting…" : "Submit application"}
        </button>
      </form>
    </div>
  );
}
